import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  TextField,
  Box,
  Radio,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useListing, useAlert, useLanguage } from 'hooks';
import {
  addVirtualServiceOrderAsync,
  hideModal,
  getVirtualServiceOrdersAsync,
} from 'state/actions';

import { ALERT, MODAL, SERVICEMODALSTEP, SERVICE_OFFER_NAME, ENG } from 'config/constants';
import { HvModal, HvPleaseWait, HvButton } from 'components';
import VirtualServiceOrder from 'models/VirtualServiceOrder';
import { VirtualServiceOrderItem, Picture } from 'models';
import { remove } from 'lodash';
import { InputChangeEvent } from 'types';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 550,
  },
  picture: {
    width: 200,
  },
  backButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  option: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  items: {
    overflowY: 'scroll',
    maxHeight: 500,
  },
  code: {
    width: '100%',
    marginRight: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
});
interface Props {
  setActiveStep: (_: number) => void;
  virtualServiceOrder: VirtualServiceOrder;
  selectedService: any;
}
function VirtualServiceSummary({ setActiveStep, virtualServiceOrder, selectedService }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const language = useLanguage();
  const { token } = useAuth();
  const { user } = useAuth();
  const { displayAlert } = useAlert();
  const path = window.location.pathname;
  const isEn = language === ENG;
  const [load, setLoad] = useState(false);
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { addedVirtualServiceOrderStatus, listing } = useListing(listing_token);
  const items = virtualServiceOrder.virtualServiceOrderItems;
  const fmtPhotos = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.photos',
  });
  const fmtPriceSummary = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.optionChoice',
  });
  const fmtSubmit = intl.formatMessage({ id: 'deliver.photos.submit' });
  const fmtBack = intl.formatMessage({ id: 'deliver.photos.back' });
  const fmtQuantity = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.quantity',
  });

  useEffect(() => {
    if (addedVirtualServiceOrderStatus) {
      setLoad(false);
      if (addedVirtualServiceOrderStatus === 200) {
        displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'deliver.photos.success' }));
      } else {
        displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'deliver.photos.error' }));
      }
      dispatch(hideModal(MODAL.VIRTUAL_STAGING));
      dispatch(getVirtualServiceOrdersAsync(token, listing.id, user.id));
    }
  }, [addedVirtualServiceOrderStatus]);
  const onBack = () => {
    setActiveStep(SERVICEMODALSTEP.OPTION);
  };

  const onClose = () => {
    dispatch(hideModal(MODAL.VIRTUAL_STAGING));
  };
  const onAdd = async () => {
    setLoad(true);
    dispatch(addVirtualServiceOrderAsync(token, virtualServiceOrder, user.id));
  };

  const getOrderSubtotal = (virtualServiceOrder: any) => {
    const quantity = items.length;
    const subtotal = virtualServiceOrder?.price * quantity;
    return subtotal.toFixed(2);
  };

  const getOrderTaxes = (virtualServiceOrder: any) => {
    const quantity = items.length;
    return (virtualServiceOrder?.price * quantity * 0.14975).toFixed(2);
  };

  const getOrderTotal = (virtualServiceOrder: any) => {
    const quantity = items.length;
    return (virtualServiceOrder?.price * quantity * 1.14975).toFixed(2);
  };
  const validateOrder = () => {
    if (!selectedService) {
      return true;
    }
    if (selectedService.virtualServiceOrderItems) {
      const foundEmptyOptionAItem = items.find((item) => {
        return item.isDelegated === true && item.note === '';
      });
      const foundEmptyOptionBItem = items.find((item) => {
        return item.isDelegated === false && item.code === '';
      });
      if (foundEmptyOptionBItem || foundEmptyOptionAItem) {
        return true;
      }
    }
    return false;
  };
  const buttons = (
    <Box>
      <HvButton className={styles.backButton} onClick={onBack}>
        {fmtBack}
      </HvButton>
      <HvButton disabled={validateOrder()} onClick={onAdd}>
        {fmtSubmit}
      </HvButton>
    </Box>
  );
  return (
    <HvModal
      title={fmtPriceSummary}
      headerImage='virtualStagingBg'
      buttons={buttons}
      onClose={onClose}
      maxWidth='md'
    >
      <Box className={styles.content}>
        <Grid container className={styles.row}>
          <Grid item>
            <Typography>{`${items.length} ${fmtPhotos}`}</Typography>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography>
                    {intl.formatMessage({
                      id: 'deliver.photos.virtualServiceModal.photos',
                    })}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Typography>{fmtQuantity}</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography>
                    {intl.formatMessage({
                      id: 'deliver.photos.virtualServiceModal.price',
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography>
                    {isEn ? selectedService.display_name : selectedService.display_name_fr}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Typography>{items.length}</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography>{`$${selectedService.price}`}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography>
                    {intl.formatMessage({
                      id: 'deliver.photos.virtualServiceModal.subtotal',
                    })}
                  </Typography>
                </TableCell>
                <TableCell align='center' />
                <TableCell align='right'>
                  <Typography>{`$${getOrderSubtotal(selectedService)}`}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography>
                    {intl.formatMessage({
                      id: 'deliver.photos.virtualServiceModal.taxes',
                    })}
                  </Typography>
                </TableCell>
                <TableCell align='center' />
                <TableCell align='right'>
                  <Typography>{`$${getOrderTaxes(selectedService)}`}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography>
                    {intl.formatMessage({
                      id: 'deliver.photos.virtualServiceModal.total',
                    })}
                  </Typography>
                </TableCell>
                <TableCell align='center' />
                <TableCell align='right'>
                  <Typography>{`$${getOrderTotal(selectedService)}`}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {load ? <HvPleaseWait /> : null}
    </HvModal>
  );
}
export default VirtualServiceSummary;
